import { BaseService } from "./BaseService";
import { Project, ErrorResponse, SuccessResponse, BuildxConfig } from "../types/index";

/**
 * Projects service for Buildx
 * Handles project management operations
 * 
 * @example
 * ```typescript
 * const projects = buildx.projects();
 * 
 * // List all projects
 * const projectList = await projects.list();
 * 
 * // Create a new project
 * const newProject = await projects.create({
 *   name: 'My Project',
 *   description: 'A new project'
 * });
 * 
 * // Get project by ID
 * const project = await projects.get('project-id');
 * ```
 */
export class Projects {
	private baseService: BaseService;

	constructor(config: BuildxConfig) {
		this.baseService = new BaseService(config);
	}

	updateConfig(config: BuildxConfig): void {
		this.baseService.updateConfig(config);
	}

	/**
	 * List all projects
	 */
	async list(): Promise<Project[] | ErrorResponse> {
		return await this.baseService.get("/projects");
	}

	/**
	 * Get project by ID
	 */
	async get(projectId: string): Promise<Project | ErrorResponse> {
		return await this.baseService.get(`/project/${projectId}`);
	}

	/**
	 * Create a new project
	 */
	async create(projectData: Partial<Project>): Promise<Project | ErrorResponse> {
		return await this.baseService.post("/projects", projectData);
	}

	/**
	 * Update project
	 */
	async update(projectId: string, projectData: Partial<Project>): Promise<Project | ErrorResponse> {
		return await this.baseService.patch(`/project/${projectId}`, projectData);
	}

	/**
	 * Delete project
	 */
	async deleteById(projectId: string): Promise<SuccessResponse | ErrorResponse> {
		return await this.baseService.delete(`/project/${projectId}`);
	}

	/**
	 * Backup project
	 */
	async backup(projectId: string): Promise<SuccessResponse | ErrorResponse> {
		const response = await this.baseService.axiosInstance.get(`/project/${projectId}/backup`, {
			responseType: "blob",
			headers: await this.baseService.getHeaders(),
		});

		// Create download link for browser
		if (typeof window !== "undefined") {
			const url = window.URL.createObjectURL(response.data);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `backup_${projectId}.buildx`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		}

		return { success: true, message: "Backup downloaded successfully" };
	}

	/**
	 * Restore project from backup
	 */
	async restore(projectId: string, file: File): Promise<SuccessResponse | ErrorResponse> {
		const formData = new FormData();
		formData.append("new_project_id", projectId);
		formData.append("backup_file", file);

		const headers = { ...(await this.baseService.getHeaders()), "Content-Type": "multipart/form-data" };
		return await this.baseService.post("/project/import", formData, { headers });

	}
} 